import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter({ data }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Inserisci un indirizzo email valido");
      return;
    }
    setLoading(true);
    try {
      const { data: res } = await axios.post(`${API}/subscribers`, { email });
      if (res.status === "already_subscribed") {
        toast.info("Questa email è già iscritta.", {
          description: "Riceverai i prossimi aggiornamenti in silenzio.",
        });
      } else {
        toast.success("Sei nella lista.", {
          description: "Controlla la tua email — ti abbiamo appena mandato la conferma.",
        });
      }
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Iscrizione non riuscita. Riprova tra un attimo.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-y border-white/5"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-px bg-accent" />
            <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
              {data.eyebrow}
            </span>
          </div>
          <h2 className="font-heading uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9] mb-6">
            {data.title}
          </h2>
          <p className="font-body text-base md:text-lg text-white/60 max-w-xl leading-relaxed">
            {data.description}
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-5 w-full"
          data-testid="newsletter-form"
        >
          <div className="relative border border-white/15 focus-within:border-accent transition-colors duration-300 bg-white/[0.02]">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={data.placeholder}
              data-testid="newsletter-input"
              className="w-full bg-transparent text-white placeholder:text-white/30 font-body text-base md:text-lg px-6 py-6 pr-40 outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-3 bg-white text-black hover:bg-accent transition-colors px-5 py-3 disabled:opacity-60"
            >
              <span className="font-heading text-sm md:text-base tracking-[0.15em] uppercase">
                {loading ? "..." : data.cta}
              </span>
              <ArrowRight size={14} />
            </button>
          </div>
          <p className="mt-4 font-body text-[10px] uppercase tracking-[0.25em] text-white/40">
            Niente spam. Ti disiscrivi con un clic.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
